import { Link } from 'react-router-dom'
import { useUtmPassthrough } from '@/hooks/useUtmPassthrough'
import { appSignupLink } from '@/utils/signupUrl'
import { trackSignupCtaClick } from '@/utils/rum'
import { moduleCards } from '@/data/modules'

interface MobileNavProps {
  isOpen: boolean
  onClose: () => void
  links: { label: string; href: string }[]
}

export default function MobileNav({ isOpen, onClose, links }: MobileNavProps) {
  const utm = useUtmPassthrough()
  const signup = appSignupLink({ params: utm })

  if (!isOpen) return null

  const handleCtaClick = () => {
    trackSignupCtaClick({ location: 'mobile_nav', hasUtm: Object.keys(utm).length > 0 })
    onClose()
  }

  const ctaClass =
    'mt-6 w-full text-center bg-button-teal text-white py-4 rounded-lg font-sans font-semibold text-base no-underline transition-all duration-300'

  return (
    <div className="fixed top-20 left-0 right-0 bottom-0 bg-white/[0.98] backdrop-blur-[20px] z-[99] flex flex-col gap-2 pt-10 pb-12 px-6 overflow-y-auto min-[900px]:hidden">
      {links.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          onClick={onClose}
          className="w-full text-slate-900 text-2xl font-serif py-4 no-underline transition-colors duration-300 hover:text-accent-teal border-b border-slate-200"
        >
          {link.label}
        </Link>
      ))}

      {/* Module pages: each feature page reachable from the menu */}
      <p className="text-xs uppercase tracking-[0.1em] text-slate-400 font-semibold mt-5 mb-1">
        Modules
      </p>
      {moduleCards.map((module) => (
        <Link
          key={module.slug}
          to={`/product/${module.slug}`}
          onClick={onClose}
          className="w-full text-slate-700 text-lg py-2.5 no-underline transition-colors duration-300 hover:text-accent-teal"
        >
          {module.name}
        </Link>
      ))}

      {/* Mobile CTA */}
      {signup.external ? (
        <a
          href={signup.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleCtaClick}
          className={ctaClass}
        >
          Start Free Trial
        </a>
      ) : (
        <Link to={signup.href} onClick={handleCtaClick} className={ctaClass}>
          Start Free Trial
        </Link>
      )}

      {/* Book a Demo secondary link */}
      <Link
        to="/demo"
        onClick={onClose}
        className="mt-3 w-full text-center border border-slate-200 text-slate-600 py-4 rounded-lg font-sans font-semibold text-[15px] no-underline transition-all duration-300 hover:bg-slate-50 hover:border-slate-300"
      >
        Book a Demo
      </Link>

      {/* Login */}
      <a
        href="https://app.wealthspace.co.uk"
        className="mt-4 w-full text-center text-sm text-slate-400 no-underline"
      >
        Already have an account? Log in
      </a>
    </div>
  )
}
